import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
	AfterViewChecked,
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	DOCUMENT,
	effect,
	ElementRef,
	inject,
	input,
	model,
	OnDestroy,
	output,
	PLATFORM_ID,
	Signal,
	ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { createPopper, Instance, Placement } from '@popperjs/core';
import { Subject, takeUntil } from 'rxjs';

import { ClickedDirective } from '@valk-nx/core/lib/directives/clicked/clicked.directive';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	exportAs: 'vpPopover',
	selector: `vp-popover`,
	templateUrl: './popover.html',
	standalone: false,
})
export class PopoverComponent
	implements AfterViewChecked, AfterViewInit, OnDestroy
{
	readonly platformId = inject(PLATFORM_ID);
	private readonly doc = inject(DOCUMENT);
	private readonly cd = inject(ChangeDetectorRef);
	private readonly viewport = inject(ViewPortService);

	alignment = input<Placement>('bottom-start');
	disabled = input(false);
	showPopover = model(false);
	showHeader = input(true);
	boundaryTargetId = input('');
	useFullWidthOfBoundaryTarget = input(true);
	popoverId = input('');
	triggerId = input('');
	popoverOffset = input<[number, number]>([0, 8]);
	closeable = input(true);
	className = input('');
	isFullHeight = input(true);
	fullHeightContainer = input(false);
	overruleCreateInstance = input(false);

	isOpen = output<boolean>();

	@ViewChild('footer') footerElement!: ElementRef<HTMLDivElement>;
	footerHeight = 0;
	@ViewChild('headerRef') headerElement!: ElementRef<HTMLDivElement>;
	headerHeight = 0;
	@ViewChild('overlayElement') overlayElement?: ElementRef<HTMLDivElement>;
	mainContentHeight = '100vh';

	@ContentChild(ClickedDirective)
	clickableElement!: ClickedDirective;

	popperInstance!: Instance;
	isSSRMode = false;

	boundaryInstance!: HTMLElement | null;
	popoverInstance!: HTMLElement;
	triggerInstance!: Element;

	isSmallTablet: Signal<boolean | undefined>;

	destroy$ = new Subject<void>();

	constructor() {
		this.isSmallTablet = toSignal(this.viewport.isSmallTablet$);

		effect(() => {
			const isOpen = this.showPopover();
			const isSmallTabletView = this.isSmallTablet();

			if (isSmallTabletView) {
				if (isOpen) {
					this.doc.body.classList.add('overflow-hidden');
				} else {
					this.doc.body.classList.remove('overflow-hidden');
				}
			}
		});
	}

	ngAfterViewChecked(): void {
		/* istanbul ignore else */
		if (isPlatformBrowser(this.platformId)) {
			this.boundaryInstance = this.doc.getElementById(
				this.boundaryTargetId(),
			);
			this.popoverInstance = this.doc.getElementById(
				this.popoverId(),
			) as HTMLElement;
			this.triggerInstance = this.doc.getElementById(
				this.triggerId(),
			) as Element;

			// Move overlay to body if it exists and isn't already there
			if (
				this.overlayElement?.nativeElement &&
				this.overlayElement.nativeElement.parentElement !==
					this.doc.body
			) {
				this.doc.body.appendChild(this.overlayElement.nativeElement);
			}

			// Move popover to body on small tablet
			if (
				this.isSmallTablet() &&
				this.popoverInstance &&
				this.popoverInstance.parentElement !== this.doc.body
			) {
				this.doc.body.appendChild(this.popoverInstance);
			}

			const strategy = this.isSmallTablet() ? 'fixed' : 'absolute';
			if (!this.popperInstance || this.overruleCreateInstance()) {
				this.popperInstance = createPopper(
					this.triggerInstance,
					this.popoverInstance,
					{
						strategy,
						modifiers: [
							{
								name: 'offset',
								options: {
									offset: this.popoverOffset(),
								},
							},
							{
								name: 'preventOverflow',
								options: {
									boundary: this.boundaryInstance,
								},
							},
							{ enabled: false, name: 'flip' },
							{
								name: 'matchWidthToBoundaryInstance',
								enabled: true,
								fn: ({ state }) => {
									/* istanbul ignore next */
									if (
										this.boundaryInstance &&
										this.useFullWidthOfBoundaryTarget()
									) {
										state.elements['popper'].classList.add(
											'boundary-width',
										);
										state.styles['popper'].width =
											`${this.boundaryInstance.offsetWidth}px`;
									} else {
										state.elements[
											'popper'
										].classList.remove('boundary-width');
									}
								},
								phase: 'beforeWrite',
								requires: ['computeStyles'],
							},
						],
						placement: this.alignment(),
					},
				);
			}
		}
	}

	ngAfterViewInit(): void {
		if (this.clickableElement) {
			this.clickableElement.clicked
				.pipe(takeUntil(this.destroy$))
				.subscribe(() => {
					this.toggle();
				});
		}
		this.isSSRMode = isPlatformServer(this.platformId);
		/* istanbul ignore else */
		if (isPlatformBrowser(this.platformId)) {
			const resizeObserver = new ResizeObserver(() => {
				this.updateHeights();
			});
			/* istanbul ignore else */
			if (this.headerElement) {
				resizeObserver.observe(this.headerElement.nativeElement);
			}
			/* istanbul ignore else */
			if (this.footerElement) {
				resizeObserver.observe(this.footerElement.nativeElement);
			}
		}
	}

	ngOnDestroy() {
		this.popperInstance?.destroy();
		this.doc.body.classList.remove('overflow-hidden');
		this.destroy$.next();
		this.destroy$.complete();
	}

	show() {
		if (!this.disabled()) {
			this.showPopover.set(true);
			this.isOpen.emit(true);
			this.cd.detectChanges();
		}
	}

	hide() {
		this.showPopover.set(false);
		this.isOpen.emit(false);
		this.cd.detectChanges();
	}

	toggle() {
		if (this.showPopover()) {
			this.hide();
		} else {
			this.show();
		}
		this.popperInstance.forceUpdate();
	}

	onClickOutside() {
		if (!this.isSmallTablet()) {
			this.hide();
		}
	}

	updateHeights() {
		this.headerHeight = this.headerElement?.nativeElement.offsetHeight ?? 0;
		this.footerHeight = this.footerElement?.nativeElement.offsetHeight ?? 0;
		this.mainContentHeight = `calc(100vh - ${this.headerHeight + this.footerHeight + 100}px)`;
		this.cd.detectChanges();
	}
}
